import type {
  ItineraryPointQuickEntryItem,
  ItineraryResourceEntryOptions,
  ItineraryResourceType,
  ItineraryTransportOption,
} from "./types";

/** 七类资源的快捷录入示例；住宿示例按实际行程候选动态生成。 */
export const ITINERARY_RESOURCE_QUICK_EXAMPLES: Readonly<Record<
  ItineraryResourceType,
  readonly string[]
>> = Object.freeze({
  住宿: Object.freeze([]),
  大交通: Object.freeze([]),
  包车: Object.freeze([
    "1-14 包车 39座 2x15500;",
    "1,14 接送 39座 2x1200,57座 1x1500;",
    "1 单送 39座 2x1200,57座 1x1500;",
    "14 单接 42座 2x1100,57座 1x1500;",
  ]),
  考察点: Object.freeze([]),
  餐: Object.freeze([]),
  导游: Object.freeze(["导游餐补 1人 7天 60", "司机餐补 1人 7天 60", "导服费 导游 500 14天"]),
  杂费: Object.freeze([
    "单房差 x元",
    "开票税金 x元",
    "横幅 x元",
    "快递费 x元",
    "电话费 x元",
    "蜀地资金池 学生x元",
    "领队打车 x元",
    "保险 学生x元",
    "导游资金池 学生x元",
    "运营费 学生x元",
    "企业资金池 学生x元",
    "非遗资金池 学生x元",
  ]),
});

/** 行程候选为空时仍展示的格式示例；这些文本不代表可关联的真实资源。 */
const ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS: Readonly<
  Partial<Record<ItineraryResourceType, string>>
> = Object.freeze({
  住宿: "住宿区域 学生 商务连锁 2晚 80，老师 商务连锁 2晚 1x100，导游 商务连锁 2晚 1x50，司机 商务连锁 2晚 80;",
  大交通: "火车 出发地-到达地 学生 100",
  考察点: "@考察点 20人 100元",
  餐: "@住宿区域 学生 正餐 6餐 50",
});

const TRANSPORT_FEE_QUICK_EXAMPLE_FALLBACK = "火车 出发地-到达地 学生 手续费 5";

/** 判断快捷项是否只是格式模板，避免用户误把占位文本当成真实候选。 */
export function isItineraryResourceQuickExampleFallback(
  resourceType: string,
  example: string,
): boolean {
  return isItineraryResourceType(resourceType)
    && (
      ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS[resourceType] === example
      || (resourceType === "大交通" && example === TRANSPORT_FEE_QUICK_EXAMPLE_FALLBACK)
    );
}

/** 全局共享的固定资源场景顺序。 */
export const ITINERARY_RESOURCE_SCENE_ORDER: readonly ItineraryResourceType[] = Object.freeze([
  "住宿",
  "大交通",
  "包车",
  "考察点",
  "餐",
  "导游",
  "杂费",
]);

const ITINERARY_RESOURCE_TYPE_NAMES: ReadonlySet<string> = new Set(
  ITINERARY_RESOURCE_SCENE_ORDER,
);

/** 动态候选名不得包含多行录入协议使用的分隔符。 */
export function isSafeItineraryResourceCandidateName(name: string): boolean {
  return name.trim().length > 0 && !/[;；\r\n]/.test(name);
}

/** 生成可见且可由解析器精确回绑的结构化大交通候选前缀。 */
export function transportQuickEntryPrefix(item: ItineraryTransportOption): string {
  const typeLabel = item.mode === "rail" ? "火车" : "飞机";
  const identity = [item.routeCode.trim(), item.seatClass.trim()].filter(Boolean).join(" ");
  return `${typeLabel} ${item.fromName}-${item.toName}${identity ? ` ${identity}` : ""}`;
}

const POINT_TYPE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  entrance_ticket: "门票",
  sub_attraction: "小景点",
  cableway: "索道",
  scenic_transport: "景交车",
  boat: "游船",
  mini_train: "小火车",
  guide: "讲解服务",
  audio_guide: "耳麦（讲解）",
  experience: "体验课程",
  performance: "表演",
});

const POINT_RESOURCE_ENTRY_TYPES: Readonly<Record<string, string>> = Object.freeze({
  entrance_ticket: "门票",
  sub_attraction: "小景点",
  cableway: "索道",
  scenic_transport: "景交车",
  boat: "游船",
  mini_train: "小火车",
  guide: "讲解服务",
  audio_guide: "讲解服务",
  experience: "体验/课程",
  performance: "表演",
});

/** 返回考察点快捷项实际展示的类型标签；未知数据库类型使用 SKU 名称。 */
export function pointQuickEntryTypeLabel(
  item: Pick<ItineraryPointQuickEntryItem, "ticketResourceType" | "skuName">,
): string {
  return POINT_TYPE_LABELS[item.ticketResourceType]
    ?? (item.skuName.trim() || item.ticketResourceType);
}

/** 生成同类型同角色组内的第一阶段候选标签。 */
function pointQuickEntryCandidateVisibleTypeLabel(
  item: ItineraryPointQuickEntryItem,
  siblingItems: readonly ItineraryPointQuickEntryItem[],
): string {
  const baseLabel = pointQuickEntryTypeLabel(item);
  const sameTypeAndRole = siblingItems.filter((candidate) => (
    candidate.travelerRole === item.travelerRole
    && pointQuickEntryTypeLabel(candidate) === baseLabel
  ));
  if (sameTypeAndRole.length <= 1) return baseLabel;

  const normalizedSkuName = item.skuName.trim();
  const sameSkuNameCount = sameTypeAndRole.filter(
    (candidate) => candidate.skuName.trim() === normalizedSkuName,
  ).length;
  const disambiguator = normalizedSkuName && sameSkuNameCount === 1
    ? normalizedSkuName
    : `SKU:${item.skuId}`;
  return `${baseLabel} ${disambiguator}`;
}

/**
 * 返回同一点同角色内全局唯一的可见标签；第一阶段按类型组消歧后，
 * 再跨所有类型检查候选标签碰撞，碰撞项统一追加完整 SKU ID。
 */
export function pointQuickEntryVisibleTypeLabel(
  item: ItineraryPointQuickEntryItem,
  siblingItems: readonly ItineraryPointQuickEntryItem[],
): string {
  const candidateLabel = pointQuickEntryCandidateVisibleTypeLabel(item, siblingItems);
  const finalCollisionCount = siblingItems.filter((candidate) => (
    candidate.travelerRole === item.travelerRole
    && pointQuickEntryCandidateVisibleTypeLabel(candidate, siblingItems) === candidateLabel
  )).length;
  return finalCollisionCount > 1
    ? `${candidateLabel} SKU:${item.skuId}`
    : candidateLabel;
}

/** 返回核价行使用的规范考察点类型；未知数据库类型保留其 SKU 展示名。 */
export function pointQuickEntryResourceEntryType(
  item: Pick<ItineraryPointQuickEntryItem, "ticketResourceType" | "skuName">,
): string {
  return POINT_RESOURCE_ENTRY_TYPES[item.ticketResourceType]
    ?? pointQuickEntryTypeLabel(item);
}

/** 判断外部字符串是否属于共享模块支持的七类资源场景。 */
export function isItineraryResourceType(value: unknown): value is ItineraryResourceType {
  return typeof value === "string"
    && ITINERARY_RESOURCE_TYPE_NAMES.has(value);
}

/** 获取资源类型快捷示例；住宿只使用当前行程真实住宿点。 */
export function quickExamplesForItineraryResource(
  resourceType: string,
  entryOptions?: ItineraryResourceEntryOptions,
  itineraryDayCount?: number,
): string[] {
  if (resourceType === "住宿") {
    const examples = (entryOptions?.stayAreas ?? [])
      .filter((item) => Number.isFinite(item.nightCount) && item.nightCount > 0 && isSafeItineraryResourceCandidateName(item.name))
      .filter((item) => {
        const parsedPrice = Number(item.recommendedPrice ?? 0);
        return !Number.isFinite(parsedPrice) || parsedPrice >= 0;
      })
      .map((item) => {
        const parsedPrice = Number(item.recommendedPrice ?? 0);
        const studentPrice = Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;
        return `${item.name} 学生 商务连锁 ${item.nightCount}晚 ${studentPrice}，老师 商务连锁 ${item.nightCount}晚 1x100，导游 商务连锁 ${item.nightCount}晚 1x50，司机 商务连锁 ${item.nightCount}晚 ${studentPrice};`;
      });
    return examples.length > 0 ? examples : [ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS.住宿!];
  }
  if (resourceType === "大交通") {
    const transports = (entryOptions?.transports ?? [])
      .filter((item) => (
        isSafeItineraryResourceCandidateName(item.fromName) && isSafeItineraryResourceCandidateName(item.toName)
        && (!item.routeCode || isSafeItineraryResourceCandidateName(item.routeCode))
        && (!item.seatClass || isSafeItineraryResourceCandidateName(item.seatClass))
      ));
    const routeExamples = transports.map((item) => {
      const price = Number(item.recommendedPrice?.trim());
      const priceText = Number.isFinite(price) && price > 0 ? ` ${price}` : "";
      return `${transportQuickEntryPrefix(item)} 学生${priceText}`;
    });
    const feeExamples = transports.map(
      (item) => `${transportQuickEntryPrefix(item)} 学生 手续费 5`,
    );
    return routeExamples.length > 0
      ? [...routeExamples, ...feeExamples]
      : [ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS.大交通!, TRANSPORT_FEE_QUICK_EXAMPLE_FALLBACK];
  }
  if (resourceType === "考察点") {
    const examples = (entryOptions?.points ?? [])
      .filter((item) => isSafeItineraryResourceCandidateName(item.name))
      .flatMap((point) => {
        if (!point.items?.length) return [`@${point.name} 20人 100元`];
        return point.items.flatMap((item) => {
          const skuName = item.skuName.trim();
          const roleLabel = item.travelerRole === "student"
            ? "学生"
            : item.travelerRole === "teacher"
              ? "老师"
              : null;
          const rawPrice = item.recommendedPrice?.trim();
          const parsedPrice = rawPrice ? Number(rawPrice) : Number.NaN;
          if (!isSafeItineraryResourceCandidateName(skuName) || !roleLabel) return [];
          const priceText = Number.isFinite(parsedPrice) && parsedPrice > 0 ? ` ${parsedPrice}` : "";
          return [`@${point.name} #${skuName} ${roleLabel}${priceText}`];
        });
      });
    return examples.length > 0 ? examples : [ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS.考察点!];
  }
  if (resourceType === "餐") {
    const examples = (entryOptions?.stayAreas ?? [])
      .filter((item) => isSafeItineraryResourceCandidateName(item.name))
      .flatMap((item) => [
        `@${item.name} 学生 正餐 6餐 50`,
        `@${item.name} 老师 正餐 6餐 80`,
      ]);
    return examples.length > 0 ? examples : [ITINERARY_RESOURCE_QUICK_EXAMPLE_FALLBACKS.餐!];
  }
  if (resourceType === "导游" && Number.isFinite(itineraryDayCount) && Number(itineraryDayCount) > 0) {
    const days = Math.max(1, Math.round(Number(itineraryDayCount)));
    return [
      `导游餐补 1人 ${days}天 60`,
      `司机餐补 1人 ${days}天 60`,
      `导服费 导游 500 ${days}天`,
    ];
  }
  if (!isItineraryResourceType(resourceType)) return [];
  return [...ITINERARY_RESOURCE_QUICK_EXAMPLES[resourceType]];
}

/** 共享场景的录入帮助与计算公式。 */
export type ItineraryResourceSceneMeta = {
  label: string;
  help: string;
  formula: string;
};

const ITINERARY_RESOURCE_SCENE_META: Record<
  ItineraryResourceType,
  ItineraryResourceSceneMeta
> = {
  住宿: { label: "区域/住宿标准", help: "预算阶段只知道大区域和住宿标准，按角色人数、晚数与人晚单价录入。", formula: "角色人数 x 晚数 x 人晚单价" },
  大交通: { label: "飞机/火车", help: "按交通方式与人数核算往返大交通，单程/往返按一次计。", formula: "人数 x 单价" },
  包车: { label: "包车/接送", help: "整段包车按车辆数与包干单价核算；班线等带角色单价按对应人数联动。", formula: "车辆数 x 包干单价，或人数 x 单价" },
  考察点: { label: "考察点/项目", help: "门票/索道按角色人数与次数核算，区分学生票与全票。", formula: "人数 x 次数 x 单价" },
  餐: { label: "区域/餐次/餐标", help: "正餐按角色人数与餐数核算，区分学生餐标与老师餐标。", formula: "人数 x 餐数 x 餐标" },
  导游: { label: "司导摊销费", help: "司导摊销按人数与天数核算餐补/补贴，全团摊销。", formula: "人数 x 天数 x 单价" },
  杂费: { label: "杂项/整包钱", help: "运营杂费按项或按人数核算，可直接录入总额；开票税金属于杂费可手工录入，营业税由系统自动计算。", formula: "人数 x 单价或直接录入金额" },
};

const ITINERARY_RESOURCE_SCENE_META_FALLBACK: ItineraryResourceSceneMeta = {
  label: "自定义项目",
  help: "按数量与单价录入预算，识别后可在明细中手动修正。",
  formula: "数量 x 单价",
};

/** 获取资源类型录入元数据，未配置时回落到通用文案。 */
export function itineraryResourceSceneMeta(
  resourceType: string,
): ItineraryResourceSceneMeta {
  return isItineraryResourceType(resourceType)
    ? ITINERARY_RESOURCE_SCENE_META[resourceType]
    : ITINERARY_RESOURCE_SCENE_META_FALLBACK;
}
